import { useEffect, useMemo, useState } from 'react'
import type { SupplyOffering, SupplyReservation } from '@zetyar/domain'
import { CalendarDays, Pencil, Plus, ShoppingCart, Trash2, Truck } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webSupplyRepository } from '@/infrastructure/repositories/supplies'
import { Badge } from '@/presentation/components/ui/Badge'
import { Button } from '@/presentation/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'

interface SaleFormState {
  readonly periodName: string
  readonly periodLimit: string
  readonly productName: string
  readonly productUnit: string
  readonly title: string
  readonly unitPrice: string
  readonly totalStock: string
  readonly startDate: string
  readonly endDate: string
}

interface PeriodFormState {
  readonly name: string
  readonly limit: string
  readonly startDate: string
  readonly endDate: string
}

interface SalePeriodMeta {
  readonly salePeriodId: string
  readonly salePeriodName: string
  readonly periodMaxPerUser: number
}

interface SalePeriodGroup {
  readonly id: string
  readonly name: string
  readonly limit: number
  readonly startDate: string
  readonly endDate: string
  readonly offerings: SupplyOffering[]
}

const today = new Date().toISOString().slice(0, 10)
const emptyForm: SaleFormState = {
  periodName: '',
  periodLimit: '1',
  productName: '',
  productUnit: 'unidad',
  title: '',
  unitPrice: '',
  totalStock: '',
  startDate: today,
  endDate: today,
}
const emptyPeriodForm: PeriodFormState = {
  name: '',
  limit: '1',
  startDate: today,
  endDate: today,
}
const activeReservationStatuses = new Set(['pending', 'paid', 'confirmed', 'delivered'])

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount)
}

function formatDate(value: string): string {
  const [year = 0, month = 1, day = 1] = value.slice(0, 10).split('-').map(Number)
  return new Intl.DateTimeFormat('es-CL', { dateStyle: 'medium' }).format(new Date(year, month - 1, day))
}

function toStartOfDay(date: string): string {
  return new Date(`${date}T00:00:00.000Z`).toISOString()
}

function toEndOfDay(date: string): string {
  return new Date(`${date}T23:59:59.000Z`).toISOString()
}

function slug(value: string): string {
  return value.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function periodId(name: string, startDate: string, endDate: string, limit: number): string {
  return `${slug(name) || 'periodo'}-${startDate}-${endDate}-${limit}`
}

function parseSalePeriodMeta(offering: SupplyOffering): SalePeriodMeta {
  if (offering.description) {
    try {
      const parsed = JSON.parse(offering.description) as Partial<SalePeriodMeta>
      if (parsed.salePeriodId && parsed.salePeriodName && parsed.periodMaxPerUser) {
        return {
          salePeriodId: parsed.salePeriodId,
          salePeriodName: parsed.salePeriodName,
          periodMaxPerUser: parsed.periodMaxPerUser,
        }
      }
    } catch {
    }
  }

  return {
    salePeriodId: periodId(offering.title, offering.startDate.slice(0, 10), offering.endDate.slice(0, 10), offering.maxPerUser),
    salePeriodName: offering.title,
    periodMaxPerUser: offering.maxPerUser,
  }
}

function serializeSalePeriodMeta(meta: SalePeriodMeta): string {
  return JSON.stringify(meta)
}

export function SalesPage() {
  const { currentMembership, currentOrganization, permissions, profile, user } = useApp()
  const [offerings, setOfferings] = useState<SupplyOffering[]>([])
  const [myReservations, setMyReservations] = useState<SupplyReservation[]>([])
  const [allReservations, setAllReservations] = useState<SupplyReservation[]>([])
  const [quantities, setQuantities] = useState<Record<string, string>>({})
  const [form, setForm] = useState<SaleFormState>(emptyForm)
  const [lockedPeriod, setLockedPeriod] = useState<SalePeriodGroup | null>(null)
  const [editingOffering, setEditingOffering] = useState<SupplyOffering | null>(null)
  const [editingPeriod, setEditingPeriod] = useState<SalePeriodGroup | null>(null)
  const [periodForm, setPeriodForm] = useState<PeriodFormState>(emptyPeriodForm)
  const [isPeriodEditorOpen, setIsPeriodEditorOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const canManage = profile?.isSuperAdmin || permissions.includes('supplies.manage')

  const tenantContext = () => {
    if (!currentOrganization || !profile || !user) return null
    return createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })
  }

  const offeringById = useMemo(() => new Map(offerings.map((offering) => [offering.id, offering])), [offerings])

  const periodGroups = useMemo(() => {
    const groups = new Map<string, SalePeriodGroup>()
    for (const offering of offerings) {
      const meta = parseSalePeriodMeta(offering)
      const existing = groups.get(meta.salePeriodId)
      if (existing) {
        existing.offerings.push(offering)
        continue
      }
      groups.set(meta.salePeriodId, {
        id: meta.salePeriodId,
        name: meta.salePeriodName,
        limit: meta.periodMaxPerUser,
        startDate: offering.startDate,
        endDate: offering.endDate,
        offerings: [offering],
      })
    }

    return [...groups.values()].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
  }, [offerings])

  const reservedByPeriod = useMemo(() => {
    const map = new Map<string, number>()
    for (const reservation of myReservations) {
      if (!activeReservationStatuses.has(reservation.status)) continue
      const offering = offeringById.get(reservation.offeringId)
      if (!offering) continue
      const meta = parseSalePeriodMeta(offering)
      map.set(meta.salePeriodId, (map.get(meta.salePeriodId) ?? 0) + reservation.quantity)
    }
    return map
  }, [myReservations, offeringById])

  const load = async () => {
    const context = tenantContext()
    if (!context) {
      setOfferings([])
      setMyReservations([])
      setAllReservations([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const [visibleOfferings, ownReservations, organizationReservations] = await Promise.all([
        webSupplyRepository.listVisibleOfferings(context, { limit: 200 }),
        webSupplyRepository.listMyReservations?.(context, 200) ?? Promise.resolve([]),
        canManage && webSupplyRepository.listReservations
          ? webSupplyRepository.listReservations(context, { limit: 200 })
          : Promise.resolve([]),
      ])
      setOfferings(visibleOfferings)
      setMyReservations(ownReservations)
      setAllReservations(organizationReservations)
      setQuantities((current) => {
        const next = { ...current }
        for (const offering of visibleOfferings) {
          if (!next[offering.id]) next[offering.id] = '1'
        }
        return next
      })
    } catch (error) {
      console.error('Error al cargar ventas:', error)
      setOfferings([])
      setMyReservations([])
      setAllReservations([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership, currentOrganization, profile, user, canManage])

  const closeEditor = () => {
    setIsEditorOpen(false)
    setLockedPeriod(null)
    setEditingOffering(null)
    setForm(emptyForm)
  }

  const openNewPeriodSale = () => {
    setLockedPeriod(null)
    setEditingOffering(null)
    setForm(emptyForm)
    setIsEditorOpen(true)
  }

  const openProductForPeriod = (period: SalePeriodGroup) => {
    setLockedPeriod(period)
    setEditingOffering(null)
    setForm({
      ...emptyForm,
      periodName: period.name,
      periodLimit: String(period.limit),
      startDate: period.startDate.slice(0, 10),
      endDate: period.endDate.slice(0, 10),
    })
    setIsEditorOpen(true)
  }

  const openEditOffering = (offering: SupplyOffering, period: SalePeriodGroup) => {
    setLockedPeriod(period)
    setEditingOffering(offering)
    setForm({
      periodName: period.name,
      periodLimit: String(period.limit),
      productName: offering.product?.name ?? '',
      productUnit: offering.product?.unit ?? 'unidad',
      title: offering.title,
      unitPrice: String(offering.unitPrice),
      totalStock: String(offering.totalStock),
      startDate: period.startDate.slice(0, 10),
      endDate: period.endDate.slice(0, 10),
    })
    setIsEditorOpen(true)
  }

  const openEditPeriod = (period: SalePeriodGroup) => {
    setEditingPeriod(period)
    setPeriodForm({
      name: period.name,
      limit: String(period.limit),
      startDate: period.startDate.slice(0, 10),
      endDate: period.endDate.slice(0, 10),
    })
    setIsPeriodEditorOpen(true)
  }

  const closePeriodEditor = () => {
    setIsPeriodEditorOpen(false)
    setEditingPeriod(null)
    setPeriodForm(emptyPeriodForm)
  }

  const savePeriod = async (event: React.FormEvent) => {
    event.preventDefault()
    const context = tenantContext()
    if (!context || !editingPeriod || !webSupplyRepository.updateOffering) return

    const periodLimit = Number(periodForm.limit)
    if (!periodForm.name.trim() || periodLimit <= 0) return

    const reservationTotals = new Map<string, number>()
    const periodOfferingIds = new Set(editingPeriod.offerings.map((offering) => offering.id))
    for (const reservation of allReservations) {
      if (!periodOfferingIds.has(reservation.offeringId)) continue
      if (!activeReservationStatuses.has(reservation.status)) continue
      reservationTotals.set(reservation.userId, (reservationTotals.get(reservation.userId) ?? 0) + reservation.quantity)
    }
    if ([...reservationTotals.values()].some((quantity) => quantity > periodLimit)) return

    const startDate = toStartOfDay(periodForm.startDate)
    const endDate = toEndOfDay(periodForm.endDate)
    const meta: SalePeriodMeta = {
      salePeriodId: periodId(periodForm.name, periodForm.startDate, periodForm.endDate, periodLimit),
      salePeriodName: periodForm.name.trim(),
      periodMaxPerUser: periodLimit,
    }

    try {
      setIsPeriodEditorOpen(false)
      await Promise.all(editingPeriod.offerings.map((offering) => webSupplyRepository.updateOffering?.(context, offering.id, {
        title: offering.title,
        description: serializeSalePeriodMeta(meta),
        unitPrice: offering.unitPrice,
        totalStock: offering.totalStock,
        remainingStock: offering.remainingStock,
        maxPerUser: periodLimit,
        startDate,
        endDate,
      })))
      closePeriodEditor()
      await load()
    } finally {
      setIsPeriodEditorOpen(false)
    }
  }

  const saveSale = async (event: React.FormEvent) => {
    event.preventDefault()
    const context = tenantContext()
    if (!context) return

    const unitPrice = Number(form.unitPrice)
    const totalStock = Number(form.totalStock)
    const periodLimit = Number(form.periodLimit)
    if (!form.periodName.trim() || !form.productName.trim() || !form.title.trim() || unitPrice < 0 || totalStock <= 0 || periodLimit <= 0) return

    const startDate = toStartOfDay(form.startDate)
    const endDate = toEndOfDay(form.endDate)
    const meta: SalePeriodMeta = {
      salePeriodId: periodId(form.periodName, form.startDate, form.endDate, periodLimit),
      salePeriodName: form.periodName.trim(),
      periodMaxPerUser: periodLimit,
    }

    try {
      setIsSaving(true)
      if (editingOffering) {
        if (!webSupplyRepository.updateOffering) return
        const reservedStock = Math.max(editingOffering.totalStock - editingOffering.remainingStock, 0)
        if (totalStock < reservedStock) return
        await webSupplyRepository.updateOffering(context, editingOffering.id, {
          title: form.title.trim(),
          description: serializeSalePeriodMeta(meta),
          unitPrice,
          totalStock,
          remainingStock: totalStock - reservedStock,
          startDate,
          endDate,
        })
      } else {
        if (!webSupplyRepository.createOffering) return
        await webSupplyRepository.createOffering(context, {
          productName: form.productName.trim(),
          productUnit: form.productUnit.trim() || 'unidad',
          title: form.title.trim(),
          description: serializeSalePeriodMeta(meta),
          unitPrice,
          currency: 'CLP',
          totalStock,
          maxPerUser: periodLimit,
          startDate,
          endDate,
        })
      }
      closeEditor()
      await load()
    } finally {
      setIsSaving(false)
    }
  }

  const reserve = async (offering: SupplyOffering) => {
    const context = tenantContext()
    if (!context || !webSupplyRepository.reserve) return

    const meta = parseSalePeriodMeta(offering)
    const requested = Number(quantities[offering.id] ?? '1')
    const usedInPeriod = reservedByPeriod.get(meta.salePeriodId) ?? 0
    const availableForMember = Math.max(meta.periodMaxPerUser - usedInPeriod, 0)
    const allowed = Math.min(availableForMember, offering.remainingStock)
    if (!Number.isInteger(requested) || requested <= 0 || requested > allowed) return

    try {
      setBusyId(offering.id)
      const { initPoint } = await webSupplyRepository.reserve(context, offering.id, requested)
      window.location.href = initPoint
    } catch (error) {
      console.error('Error al reservar la oferta:', error)
      alert(error instanceof Error ? error.message : 'No se pudo iniciar el pago de la compra.')
      setBusyId(null)
    }
  }

  const deleteSale = async (offeringId: string) => {
    const context = tenantContext()
    if (!context || !webSupplyRepository.deleteOffering) return
    if (!window.confirm('Eliminar esta venta?')) return

    try {
      setBusyId(offeringId)
      await webSupplyRepository.deleteOffering(context, offeringId)
      await load()
    } finally {
      setBusyId(null)
    }
  }

  const deliver = async (reservationId: string) => {
    const context = tenantContext()
    if (!context || !webSupplyRepository.markDelivered) return

    try {
      setBusyId(reservationId)
      await webSupplyRepository.markDelivered(context, reservationId)
      await load()
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Ventas</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{currentOrganization?.name ?? ''}</p>
        </div>
        {canManage && (
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openNewPeriodSale}>
            Nueva venta
          </Button>
        )}
      </div>

      {isLoading ? (
        <Card><CardContent className="py-10 text-center text-sm text-neutral-500">Cargando ventas...</CardContent></Card>
      ) : periodGroups.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-neutral-500">No hay ventas disponibles.</CardContent></Card>
      ) : (
        <div className="space-y-5">
          {periodGroups.map((period) => {
            const usedInPeriod = reservedByPeriod.get(period.id) ?? 0
            const availableForMember = Math.max(period.limit - usedInPeriod, 0)
            return (
              <Card key={period.id}>
                <CardHeader>
                  <div className="min-w-0">
                    <CardTitle className="truncate">{period.name}</CardTitle>
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                      <CalendarDays className="h-4 w-4" /> {formatDate(period.startDate)} / {formatDate(period.endDate)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {canManage && (
                      <>
                      <Button size="sm" variant="outline" leftIcon={<Pencil className="h-4 w-4" />} onClick={() => openEditPeriod(period)}>
                        Editar periodo
                      </Button>
                      <Button size="sm" variant="outline" leftIcon={<Plus className="h-4 w-4" />} onClick={() => openProductForPeriod(period)}>
                        Agregar producto
                      </Button>
                      </>
                    )}
                    <Badge variant={availableForMember > 0 ? 'success' : 'warning'}>{usedInPeriod}/{period.limit} por socio</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 lg:grid-cols-2">
                    {period.offerings.map((offering) => {
                      const requested = Number(quantities[offering.id] ?? '1')
                      const allowed = Math.min(availableForMember, offering.remainingStock)
                      const canReserve = requested > 0 && requested <= allowed && offering.status === 'active'

                      return (
                        <div key={offering.id} className="rounded-md border border-neutral-200 p-4 dark:border-neutral-800">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">{offering.title}</h3>
                              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{offering.product?.name ?? 'Producto'}</p>
                            </div>
                            <Badge variant={offering.status === 'active' ? 'success' : 'neutral'}>{offering.status}</Badge>
                          </div>
                          <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                            <div><p className="text-neutral-500 dark:text-neutral-400">Precio</p><p className="font-semibold">{formatAmount(offering.unitPrice, offering.currency)}</p></div>
                            <div><p className="text-neutral-500 dark:text-neutral-400">Stock</p><p className="font-semibold">{offering.remainingStock}</p></div>
                            <div><p className="text-neutral-500 dark:text-neutral-400">Disponible</p><p className="font-semibold">{allowed}</p></div>
                          </div>
                          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
                            <Input
                              label="Cantidad"
                              type="number"
                              min={1}
                              max={Math.max(allowed, 1)}
                              value={quantities[offering.id] ?? '1'}
                              onChange={(event) => setQuantities((current) => ({ ...current, [offering.id]: event.target.value }))}
                            />
                            <Button className="sm:w-40" leftIcon={<ShoppingCart className="h-4 w-4" />} disabled={!canReserve} isLoading={busyId === offering.id} onClick={() => void reserve(offering)}>
                              Comprar
                            </Button>
                            {canManage && (
                              <div className="flex gap-2 sm:flex-none">
                                <Button variant="outline" className="w-11" leftIcon={<Pencil className="h-4 w-4" />} aria-label="Editar producto" disabled={busyId === offering.id} onClick={() => openEditOffering(offering, period)} />
                                <Button variant="danger" className="w-11" leftIcon={<Trash2 className="h-4 w-4" />} aria-label="Eliminar producto" disabled={busyId === offering.id} onClick={() => void deleteSale(offering.id)} />
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Mis compras</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {myReservations.length === 0 ? <p className="text-sm text-neutral-500">Sin compras registradas.</p> : myReservations.slice(0, 8).map((reservation) => (
              <div key={reservation.id} className="flex items-center justify-between gap-3 border-b border-neutral-100 pb-3 text-sm last:border-0 last:pb-0 dark:border-neutral-800">
                <div className="min-w-0">
                  <p className="truncate font-medium text-neutral-900 dark:text-neutral-100">{reservation.offering?.title ?? 'Venta'}</p>
                  <p className="text-neutral-500 dark:text-neutral-400">{reservation.quantity} x {formatAmount(reservation.unitPrice, reservation.currency)}</p>
                </div>
                <Badge variant={reservation.status === 'cancelled' ? 'danger' : 'neutral'}>{reservation.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {canManage && (
          <Card>
            <CardHeader><CardTitle>Reservas</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {allReservations.length === 0 ? <p className="text-sm text-neutral-500">Sin reservas registradas.</p> : allReservations.slice(0, 10).map((reservation) => (
                <div key={reservation.id} className="flex items-center justify-between gap-3 border-b border-neutral-100 pb-3 text-sm last:border-0 last:pb-0 dark:border-neutral-800">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-neutral-900 dark:text-neutral-100">{reservation.offering?.title ?? 'Venta'}</p>
                    <p className="truncate text-neutral-500 dark:text-neutral-400">{reservation.user?.displayName || reservation.user?.email || 'Socio'} - {reservation.quantity} unidades</p>
                  </div>
                  {reservation.status === 'delivered' ? <Badge variant="success">Entregado</Badge> : (
                    <Button size="sm" variant="outline" leftIcon={<Truck className="h-4 w-4" />} isLoading={busyId === reservation.id} onClick={() => void deliver(reservation.id)}>
                      Entregar
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      <Modal
        isOpen={isPeriodEditorOpen}
        onClose={closePeriodEditor}
        title={editingPeriod ? `Editar ${editingPeriod.name}` : 'Editar periodo'}
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={closePeriodEditor}>Cancelar</Button>
            <Button form="period-form" type="submit">Guardar periodo</Button>
          </>
        }
      >
        <form id="period-form" className="grid gap-4 sm:grid-cols-2" onSubmit={savePeriod}>
          <Input label="Periodo" value={periodForm.name} onChange={(event) => setPeriodForm({ ...periodForm, name: event.target.value })} required />
          <Input label="Maximo por socio en el periodo" type="number" min={1} value={periodForm.limit} onChange={(event) => setPeriodForm({ ...periodForm, limit: event.target.value })} required />
          <Input label="Inicio" type="date" value={periodForm.startDate} onChange={(event) => setPeriodForm({ ...periodForm, startDate: event.target.value })} required />
          <Input label="Termino" type="date" value={periodForm.endDate} onChange={(event) => setPeriodForm({ ...periodForm, endDate: event.target.value })} required />
        </form>
      </Modal>
      <Modal
        isOpen={isEditorOpen}
        onClose={closeEditor}
        title={editingOffering ? `Editar ${editingOffering.title}` : lockedPeriod ? `Agregar producto a ${lockedPeriod.name}` : 'Nueva venta'}
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={closeEditor}>Cancelar</Button>
            <Button form="sale-form" type="submit" isLoading={isSaving}>Guardar</Button>
          </>
        }
      >
        <form id="sale-form" className="grid gap-4 sm:grid-cols-2" onSubmit={saveSale}>
          <Input label="Periodo" value={form.periodName} onChange={(event) => setForm({ ...form, periodName: event.target.value })} disabled={Boolean(lockedPeriod)} required />
          <Input label="Maximo por socio en el periodo" type="number" min={1} value={form.periodLimit} onChange={(event) => setForm({ ...form, periodLimit: event.target.value })} disabled={Boolean(lockedPeriod)} required />
          <Input label="Inicio" type="date" value={form.startDate} onChange={(event) => setForm({ ...form, startDate: event.target.value })} disabled={Boolean(lockedPeriod)} required />
          <Input label="Termino" type="date" value={form.endDate} onChange={(event) => setForm({ ...form, endDate: event.target.value })} disabled={Boolean(lockedPeriod)} required />
          <Input label="Producto" value={form.productName} onChange={(event) => setForm({ ...form, productName: event.target.value })} disabled={Boolean(editingOffering)} required />
          <Input label="Unidad" value={form.productUnit} onChange={(event) => setForm({ ...form, productUnit: event.target.value })} disabled={Boolean(editingOffering)} required />
          <Input label="Titulo" className="sm:col-span-2" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} required />
          <Input label="Precio" type="number" min={0} value={form.unitPrice} onChange={(event) => setForm({ ...form, unitPrice: event.target.value })} required />
          <Input label="Stock" type="number" min={1} value={form.totalStock} onChange={(event) => setForm({ ...form, totalStock: event.target.value })} required />
        </form>
      </Modal>
    </div>
  )
}
